import React, { useRef, useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import SectionIntro from "./SectionIntro";

const features = [
  {
    title: "features.items.panoramicViews.title",
    description: "features.items.panoramicViews.description",
    media: { type: "image", src: "/images/gallery/atlas1.jpg" },
    aspect: "landscape",
  },
  {
    title: "features.items.luxuriousAccommodations.title",
    description: "features.items.luxuriousAccommodations.description",
    media: { type: "image", src: "/images/gallery/living-room-1.jpg" },
    aspect: "portrait",
  },
  {
    title: "features.items.berberHeritage.title",
    description: "features.items.berberHeritage.description",
    media: { type: "video", src: "/images/gallery/cult.mp4" },
    aspect: "landscape",
  },
  {
    title: "features.items.mountainSanctuary.title",
    description: "features.items.mountainSanctuary.description",
    media: { type: "image", src: "/images/gallery/exterior6.jpg" },
    aspect: "portrait",
  },
];

const Features = () => {
  const videoRefs = useRef([]);
  const observerRef = useRef(null);
  const { t } = useTranslation();

  useEffect(() => {
    features.forEach((feature) => {
      if (feature.media.type === "video") {
        const video = document.createElement("video");
        video.src = feature.media.src;
        video.preload = "metadata";
        video.load();
      }
    });

    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const index = parseInt(entry.target.dataset.index);
          if (features[index].media.type !== "video") return;
          const video = videoRefs.current[index];
          if (!video) return;
          if (entry.isIntersecting && video.paused) {
            video.play().catch(() => {});
          } else if (!entry.isIntersecting && !video.paused) {
            video.pause();
          }
        });
      },
      { threshold: 0.3, rootMargin: "50px 0px 50px 0px" }
    );

    const els = document.querySelectorAll(".feature-media");
    els.forEach((el) => observerRef.current.observe(el));
    return () => observerRef.current && observerRef.current.disconnect();
  }, []);

  return (
    <section id="features" className="py-24 md:py-32 bg-cream overflow-hidden">
      <div className="max-w-7xl mx-auto px-5 md:px-10">
        <SectionIntro
          eyebrow={t("labels.theVilla")}
          title={t("features.title")}
          lead={t("features.subtitle")}
          className="mb-20 md:mb-28"
        />

        <div className="space-y-24 md:space-y-32">
          {features.map((feature, index) => {
            const reversed = index % 2 === 1;
            return (
              <div
                key={index}
                className={`feature-media flex flex-col gap-10 lg:gap-20 items-center ${
                  reversed ? "lg:flex-row-reverse" : "lg:flex-row"
                }`}
                data-index={index}
              >
                {/* Media */}
                <motion.div
                  className="w-full lg:w-1/2"
                  initial={{ opacity: 0, scale: 1.04 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
                >
                  <div
                    className={`relative overflow-hidden bg-cream-dark group ${
                      feature.aspect === "portrait"
                        ? "aspect-[4/5]"
                        : "aspect-[4/3]"
                    }`}
                  >
                    {feature.media.type === "image" ? (
                      <img
                        src={feature.media.src}
                        alt={t(feature.title)}
                        className="w-full h-full object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-105"
                        loading="lazy"
                        decoding="async"
                      />
                    ) : (
                      <video
                        ref={(el) => (videoRefs.current[index] = el)}
                        src={feature.media.src}
                        loop
                        muted
                        playsInline
                        className="w-full h-full object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-105"
                      />
                    )}
                  </div>
                </motion.div>

                {/* Content */}
                <motion.div
                  className="w-full lg:w-1/2"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.8, delay: 0.15 }}
                >
                  <div className={`max-w-lg ${reversed ? "lg:ml-auto" : ""}`}>
                    <span className="font-serif text-5xl md:text-6xl font-light text-brass/40 leading-none">
                      {String(index + 1).padStart(2, "0")}
                    </span>
                    <h3 className="mt-5 font-serif font-light text-3xl md:text-4xl text-ink leading-tight">
                      {t(feature.title)}
                    </h3>
                    <div className="rule-gold my-6" />
                    <p className="text-base md:text-lg text-ink-soft leading-relaxed font-light">
                      {t(feature.description)}
                    </p>
                  </div>
                </motion.div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Full-bleed CTA band */}
      <section className="relative mt-24 md:mt-32 py-32 md:py-44 overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: "url('/images/gallery/background.jpg')" }}
        />
        <div className="absolute inset-0 bg-ink/55" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink/80 via-ink/20 to-ink/40" />

        <div className="relative z-10 max-w-3xl mx-auto px-6 text-center text-white">
          <motion.span
            className="eyebrow text-brass-light block mb-6"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            {t("features.cta.exclusiveRetreat")}
          </motion.span>
          <motion.h3
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15, duration: 0.8 }}
            viewport={{ once: true }}
            className="font-serif font-light text-4xl md:text-5xl lg:text-6xl leading-[1.1] tracking-wide mb-8"
          >
            {t("features.cta.title")}
          </motion.h3>
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ delay: 0.3, duration: 0.8 }}
            viewport={{ once: true }}
            className="text-base md:text-lg text-white/80 max-w-xl mx-auto mb-12 leading-relaxed font-light"
          >
            {t("features.cta.description")}
          </motion.p>
          <Link
            to="/booking"
            className="inline-flex items-center justify-center bg-brass hover:bg-brass-dark text-ink text-[12px] uppercase tracking-[0.22em] py-4 px-10 transition-colors duration-300"
          >
            {t("features.cta.reserveButton")}
          </Link>
          <p className="eyebrow text-white/50 mt-10 text-[10px]">
            {t("features.cta.footerText")}
          </p>
        </div>
      </section>
    </section>
  );
};

export default Features;
